import { Coverage, DocumentReference, Reference } from 'src/careflow/ph-core/fhir-client/resources';
/**
 * --------------------------------------------------------------------------
 * Constants
 * --------------------------------------------------------------------------
 */

export const COVERAGE_CLASS_SYSTEM = 'http://terminology.hl7.org/CodeSystem/coverage-class';

export const COVERAGE_CLASS_CODE = 'plan';

export const MEDICARE_PAYOR_DISPLAY = 'medicare_Centers for Medicare & Medicaid Services (CMS)';

export const PARTS = [
  { value: 'A', label: 'Part A' },
  { value: 'B', label: 'Part B' },
  { value: 'C', label: 'Part C' },
  { value: 'D', label: 'Part D' },
];
/**
 * --------------------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------------------
 */

export const getResourceReference = (
  resource?: {
    resourceType?: string;
    id?: string;
  } | null
): string | undefined => {
  if (!resource?.resourceType || !resource?.id) {
    return undefined;
  }

  return `${resource.resourceType}/${resource.id}`;
};

export const getCoveragePart = (resource?: Coverage): string | undefined => {
  const name = resource?.class?.[0]?.name;

  if (!name) {
    return undefined;
  }

  return name.replace('Medicare Part ', '').trim() || undefined;
};

const getCoverageReference = (resourceToRelate?: any, coverage?: any): string | undefined => {
  const existingCoverageReference = getResourceReference(coverage);

  if (existingCoverageReference) {
    return existingCoverageReference;
  }

  if (Array.isArray(resourceToRelate)) {
    return getResourceReference(resourceToRelate[0]);
  }

  return getResourceReference(resourceToRelate);
};

export const getRelatedReferences = (resourceToRelate?: any, serviceRequest?: any): string[] =>
  [getResourceReference(serviceRequest), getCoverageReference(resourceToRelate)].filter(
    (reference): reference is string => Boolean(reference)
  );

export const getDocumentReference = (resource?: DocumentReference): string | undefined =>
  getResourceReference(resource);

export const hasAllReferences = (related: Reference[], references: string[]): boolean =>
  references.every((reference) => related.some((item) => item.reference === reference));

const addMissingReferences = (related: Reference[], references: string[]): Reference[] => {
  const existingReferences = new Set(
    related
      .map((item) => item.reference)
      .filter((reference): reference is string => Boolean(reference))
  );

  const missingReferences = references
    .filter((reference) => !existingReferences.has(reference))
    .map((reference) => ({ reference }));

  return [...related, ...missingReferences];
};

const removeReferences = (related: Reference[], referencesToRemove: string[]): Reference[] => {
  const references = new Set(referencesToRemove);

  return related.filter((item) => !item.reference || !references.has(item.reference));
};

export const getCoverageReferences = (resourceToRelate?: any): Reference[] => {
  const resources = Array.isArray(resourceToRelate)
    ? resourceToRelate
    : resourceToRelate
    ? [resourceToRelate]
    : [];

  return resources
    .map((resource) => getResourceReference(resource))
    .filter((reference): reference is string => Boolean(reference))
    .map((reference) => ({
      reference,
    }));
};

/**
 * Returns a new DocumentReference without the specified relationships.
 * The original FHIR resource is never mutated.
 */
export const removeDocumentRelationships = (
  document: DocumentReference,
  referencesToRemove: string[]
): DocumentReference => {
  const related = document.context?.related ?? [];

  const updatedRelated = removeReferences(related, referencesToRemove);

  if (!updatedRelated.length) {
    const { context, ...documentWithoutContext } = document;

    return documentWithoutContext;
  }

  return {
    ...document,
    context: {
      ...document.context,
      related: updatedRelated,
    },
  };
};

/**
 * Returns a new DocumentReference with all required relationships.
 * Existing relationships are preserved.
 */
export const addDocumentRelationships = (
  document: DocumentReference,
  references: string[]
): DocumentReference => {
  const related = document.context?.related ?? [];

  const updatedRelated = addMissingReferences(related, references);

  return {
    ...document,
    context: {
      ...document.context,
      related: updatedRelated,
    },
  };
};

/**
 * --------------------------------------------------------------------------
 * ServiceRequest supportingInfo
 * --------------------------------------------------------------------------
 */

export const mapDocumentReferenceToResource = (
  resource: any,
  relatedDocuments: DocumentReference[],
  removedDocuments: DocumentReference[]
): Reference[] => {
  if (!resource) {
    return [];
  }

  const existingSupportingInfo =
    resource.supportingInfo?.map((item: Reference) => item.reference) ?? [];

  const relatedDocumentReferences = relatedDocuments
    .map(getDocumentReference)
    .filter((reference): reference is string => Boolean(reference));

  const removedDocumentReferences = new Set(
    removedDocuments
      .map(getDocumentReference)
      .filter((reference): reference is string => Boolean(reference))
  );

  const allReferences = new Set([...existingSupportingInfo, ...relatedDocumentReferences]);

  return Array.from(allReferences)
    .filter((reference) => !removedDocumentReferences.has(reference))
    .map((reference) => ({
      reference,
    }));
};

/**
 * --------------------------------------------------------------------------
 * Binary helpers
 * --------------------------------------------------------------------------
 */

export const convertBlobToBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = reject;

    reader.onload = () => {
      const result = reader.result?.toString();

      if (!result) {
        reject(new Error('Unable to convert file to base64'));
        return;
      }

      const [, base64] = result.split(',');

      if (!base64) {
        reject(new Error('Unable to extract base64 content'));
        return;
      }

      resolve(base64);
    };

    reader.readAsDataURL(file);
  });
